/**
 * Cart-page cross-sell: upgrade-path swaps + complementary-product suggestions.
 *
 * Depends on Dawn globals already loaded on the cart page: `routes`,
 * `fetchConfig`, `PUB_SUB_EVENTS`, `publish`.
 *
 * - <cart-upgrade-option> is rendered once per entry in a cart item's
 *   `custom.optional_upgrade` metafield (list.variant_reference). Clicking
 *   its button adds the upgrade variant, then removes the original line.
 *
 * - <cart-complementary> is rendered once, after the items table. It looks
 *   up each unique product in the cart against Shopify's native
 *   `intent=complementary` recommendations (populated via the
 *   `complementary_products` metafield / Search & Discovery app), merges
 *   and dedupes the results, and renders up to 4 add-on suggestions.
 *
 * Both re-run automatically whenever Dawn re-renders the cart items markup
 * (quantity change, remove, or the manual `publish(PUB_SUB_EVENTS.cartUpdate, ...)`
 * calls below), since that markup replacement naturally reconnects these
 * custom elements.
 *
 * Wrapped in an IIFE and guarded against redefinition: cart-drawer.liquid
 * and main-cart-items.liquid can both load this file on the same page (the
 * drawer is rendered sitewide), and a plain top-level `class` declaration
 * would throw a SyntaxError on the second load.
 */
(function () {
class CartUpgradeOption extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.cart-upgrade-option__button');
    this.button?.addEventListener('click', this.handleUpgrade.bind(this));
  }

  async handleUpgrade() {
    if (!this.button || this.button.disabled) return;

    this.button.disabled = true;
    const originalText = this.button.textContent;
    this.button.textContent = 'Upgrading…';

    const { oldLineKey, newVariantId, quantity } = this.dataset;

    try {
      // Add the upgrade variant first. If this fails, the customer's
      // original item is untouched.
      const addResponse = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: newVariantId, quantity: parseInt(quantity, 10) || 1 }],
        }),
      });

      if (!addResponse.ok) {
        const err = await addResponse.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add upgrade to cart');
      }

      // Only remove the original line once the upgrade is confirmed added.
      const removeResponse = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: oldLineKey, quantity: 0 }),
      });

      if (!removeResponse.ok) {
        throw new Error('Upgrade added, but the original item could not be removed automatically.');
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-cross-sell' });
    } catch (e) {
      console.error(e);
      this.button.textContent = 'Unavailable';
      return;
    }

    this.button.textContent = originalText;
    this.button.disabled = false;
  }
}

if (!customElements.get('cart-upgrade-option')) {
  customElements.define('cart-upgrade-option', CartUpgradeOption);
}

class CartComplementary extends HTMLElement {
  static LIMIT = 4;

  connectedCallback() {
    this.load();
  }

  async load() {
    let cart;
    try {
      cart = await (await fetch(`${routes.cart_url}.json`)).json();
    } catch (e) {
      console.error(e);
      return;
    }

    if (!cart.items.length) {
      this.innerHTML = '';
      return;
    }

    this.currencyCode = cart.currency.active;

    // Text describing which cart line seeded each product_id — pulled from
    // both product title and variant title, since some product families
    // (like separate Standard/Reduced Barrier hose listings) distinguish
    // via the product title rather than a variant option.
    const seedTextByProductId = new Map();
    cart.items.forEach((item) => {
      if (!seedTextByProductId.has(item.product_id)) {
        const text = [item.product_title, item.variant_title].filter(Boolean).join(' ').toLowerCase();
        seedTextByProductId.set(item.product_id, text);
      }
    });

    const uniqueProductIds = [...seedTextByProductId.keys()];

    const results = await Promise.all(
      uniqueProductIds.map((id) =>
        fetch(
          `${window.Shopify.routes.root}recommendations/products.json?product_id=${id}&limit=${CartComplementary.LIMIT}&intent=complementary`
        )
          .then((res) => (res.ok ? res.json() : { products: [] }))
          .then((data) =>
            (data.products || []).map((product) => ({
              product,
              seedText: seedTextByProductId.get(id),
            }))
          )
          .catch(() => [])
      )
    );

    const seen = new Set();
    const entries = results
      .flat()
      .filter(({ product }) => {
        if (seen.has(product.id)) return false;
        seen.add(product.id);
        return true;
      })
      .map(({ product, seedText }) => ({ product, variant: this.pickVariant(product, seedText) }))
      // A multi-variant product with no confident match is dropped rather
      // than shown with a guessed (possibly wrong) variant.
      .filter(({ variant }) => variant)
      .slice(0, CartComplementary.LIMIT);

    if (!entries.length) {
      this.innerHTML = '';
      return;
    }

    this.render(entries);
  }

  pickVariant(product, seedText) {
    if (product.variants.length === 1) return product.variants[0];

    if (seedText) {
      const match = product.variants.find((variant) => {
        const variantValues = [variant.option1, variant.option2, variant.option3]
          .filter(Boolean)
          .map((value) => value.toLowerCase());
        // Does this variant's option value show up anywhere in the seed
        // item's product/variant title text? Covers "Standard" matching
        // against a seed titled "...Standard Barrier Hose".
        return variantValues.some((value) => seedText.includes(value));
      });
      if (match) return match;
    }

    return null; // multiple variants, nothing matched — don't guess
  }

  render(entries) {
    this.innerHTML = `
      <div class="cart-complementary">
        <h3 class="cart-complementary__heading h4">Also purchased with</h3>
        <ul class="cart-complementary__list list-unstyled" role="list">
          ${entries.map(({ product, variant }) => this.renderCard(product, variant)).join('')}
        </ul>
      </div>
    `;

    this.querySelectorAll('[data-add-variant-id]').forEach((button) => {
      button.addEventListener('click', () => this.addToCart(button));
    });
  }

  renderCard(product, variant) {
    const image = variant.featured_image?.url || product.featured_image?.url || (product.images && product.images[0]) || '';
    return `
      <li class="cart-complementary__item">
        <a href="${product.url}" class="cart-complementary__image-link">
          ${image ? `<img src="${image}" width="90" height="90" alt="${this.escape(product.title)}" loading="lazy">` : ''}
        </a>
        <div class="cart-complementary__info">
          <a href="${product.url}" class="cart-complementary__title">${this.escape(product.title)}${
            variant.title !== 'Default Title' ? ` &mdash; ${this.escape(variant.title)}` : ''
          }</a>
          <span class="cart-complementary__price">${this.formatMoney(variant.price)}</span>
        </div>
        <button type="button" class="button button--secondary cart-complementary__add" data-add-variant-id="${variant.id}">
          Add
        </button>
      </li>
    `;
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  formatMoney(cents) {
    // The recommendations endpoint returns raw integer cents, unlike some
    // other storefront endpoints that pre-format price as a string.
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currencyCode || 'USD',
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  async addToCart(button) {
    if (button.disabled) return;
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding…';

    try {
      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ items: [{ id: button.dataset.addVariantId, quantity: 1 }] }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-cross-sell' });
    } catch (e) {
      console.error(e);
      button.textContent = 'Unavailable';
      return;
    }

    button.textContent = originalText;
    button.disabled = false;
  }
}

if (!customElements.get('cart-complementary')) {
  customElements.define('cart-complementary', CartComplementary);
}
})();